import { useState } from 'react';
import Seo from '../../components/Seo/Seo';
import { Link } from 'react-router-dom';
import { applyCashSwitch } from '../../api/orders';

interface OrderSummary {
	orderNumber?: string; orderId?: number; switchToken?: string; payment?: 'cash' | 'card';
	name?: string; phone?: string; email?: string;
	car?: string; address?: string; day?: string; time?: string;
	battery?: string; price?: number; deliveryFee?: number; total?: number;
}

function load(): OrderSummary | null {
	try {
		const s = localStorage.getItem('akku24_last_order');
		return s ? JSON.parse(s) : null;
	} catch { return null; }
}

const fmt = (n: number) => Number(n).toLocaleString('hu-HU') + ' Ft';

export default function MegrendelesOkPage() {
	const order = load();
	const [total, setTotal] = useState<number | undefined>(order?.total);
	const [payment, setPayment] = useState<'cash' | 'card' | undefined>(order?.payment);
	const [promo, setPromo] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
	const [saving, setSaving] = useState(0);
	const [err, setErr] = useState('');

	const showPromo = !!order && payment === 'card' && !!order.orderId && !!order.switchToken && promo !== 'done';

	async function doSwitch() {
		if (!order?.orderId || !order?.switchToken) return;
		setPromo('loading'); setErr('');
		try {
			const res = await applyCashSwitch(order.orderId, order.switchToken);
			if (res.data?.result === 'OK') {
				if (typeof res.data.total === 'number') setTotal(res.data.total);
				if (typeof res.data.discount_amount === 'number') setSaving(res.data.discount_amount);
				setPayment('cash');
				setPromo('done');
			} else {
				setErr(res.data?.message || 'A kedvezményt nem sikerült alkalmazni.');
				setPromo('error');
			}
		} catch {
			setErr('Hiba történt. Kérjük próbáld újra.');
			setPromo('error');
		}
	}

	const payLabel = payment === 'card'
		? 'A helyszínen, a szerelőnél — bankkártya'
		: payment === 'cash'
			? 'A helyszínen, a szerelőnél — készpénz'
			: 'A helyszínen, a szerelőnél — készpénz vagy kártya';

	return (
		<div className="order">
			<Seo title="Köszönjük a megrendelést! - Akku 24+" noindex />
			<div className="container order__success">
				<h1>Köszönjük a megrendelést!</h1>
				{order?.orderNumber && <p className="order__ordernum">Rendelésszám: {order.orderNumber}</p>}
				<p style={{ marginBottom: 24 }}>
					Megrendelésedet rögzítettük. Ha valamit egyeztetnünk kell, munkatársunk telefonon keres a megadott
					számon. Ha minden világos, a választott időpontban érkezünk — és üzenetet küldünk, amint a szerelő elindul hozzád.
				</p>

				{order && (
					<div className="order__success-card">
						{order.car && <div className="summary-item"><span className="label">Jármű</span><span>{order.car}</span></div>}
						{order.battery && <div className="summary-item"><span className="label">Akkumulátor</span><span>{order.battery}</span></div>}
						{order.address && <div className="summary-item"><span className="label">Szerelés címe</span><span>{order.address}</span></div>}
						{(order.day || order.time) && <div className="summary-item"><span className="label">Időpont</span><span>{order.day} {order.time}</span></div>}
						{order.phone && <div className="summary-item"><span className="label">Telefonszám</span><span>{order.phone}</span></div>}
						{typeof total === 'number' && <div className="summary-row summary-total"><span>Fizetendő</span><span>{fmt(total)}</span></div>}
						<div className="summary-item"><span className="label">Fizetés</span><span>{payLabel}</span></div>
					</div>
				)}

				{showPromo && (
					<div className="promo">
						<p className="promo-text">🎁 <strong>Extra kedvezmény!</strong> Válts készpénzes fizetésre, és most <strong>5%-kal olcsóbb</strong> az akkumulátor.</p>
						<button type="button" className="btn promo-btn" onClick={doSwitch} disabled={promo === 'loading'}>
							{promo === 'loading' ? 'Kedvezmény alkalmazása…' : 'Élek a kedvezménnyel — készpénzben fizetek'}
						</button>
						{promo === 'error' && <p className="promo-err">{err}</p>}
					</div>
				)}
				{promo === 'done' && (
					<p className="promo-done">✓ <strong>Kedvezmény jóváírva!</strong> {saving > 0 ? `Megtakarítás: ${fmt(saving)}.` : ''} A fizetés a helyszínen készpénzben történik.</p>
				)}

				<div className="order__steps">
					<h2>Mi történik most?</h2>
					<ol>
						<li><strong>Egyeztetés, ha szükséges.</strong> Ha valamit pontosítanunk kell, telefonon keresünk. Ha minden világos, nem hívunk feleslegesen — a választott időpontban érkezünk.</li>
						<li><strong>Kiérkezünk.</strong> Csapatunk a választott időpontban a helyszínre megy — átlagosan 1 órán belül Budapesten. Kérjük, ne várj a gépjárművednél — amint a szerelő elindul hozzád, üzenetet küldünk. Ettől a ponttól a megrendelés már nem mondható le.</li>
						<li><strong>Csere a helyszínen.</strong> Kicseréljük az akkumulátort ~20 perc alatt, fizetni csak utána kell.</li>
					</ol>
				</div>

				<ul className="order__trust">
					<li><strong>Átlagosan 1 órán belül</strong> kiszállunk Budapesten</li>
					<li><strong>1-2 év garancia</strong> minden akkumulátorra</li>
					<li><strong>Helyszíni csere</strong> ~20 perc alatt</li>
					<li><strong>Fizetés a szerelőnél</strong> készpénz vagy kártya</li>
				</ul>

				<p style={{ marginTop: 28 }}><Link to="/" className="btn">Vissza a főoldalra</Link></p>
			</div>
		</div>
	);
}
